import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Network } from '@capacitor/network';
import { Keyboard } from '@capacitor/keyboard';
import { SplashScreen } from '@capacitor/splash-screen';
import { Style, StatusBar } from '@capacitor/status-bar';

import { AUTH } from 'src/lib/firebase';
import { refreshAuthToken } from 'src/lib/axios-artham';

/**
 * Utilities for Capacitor mobile app functionality
 */

/**
 * Check if the app is running on a native mobile platform
 */
export const isNativePlatform = (): boolean => Capacitor.isNativePlatform();

/**
 * Get the current platform (ios, android, or web)
 */
export const getPlatform = (): string => Capacitor.getPlatform();

/**
 * Check if running on iOS
 */
export const isIOS = (): boolean => Capacitor.getPlatform() === 'ios';

/**
 * Check if running on Android
 */
export const isAndroid = (): boolean => Capacitor.getPlatform() === 'android';

/**
 * Check if running in web browser
 */
export const isWeb = (): boolean => Capacitor.getPlatform() === 'web';

/**
 * Hide the native splash screen once the web app has rendered its first screen.
 *
 * The splash is configured with `launchAutoHide: false` (capacitor.config.ts), so
 * it stays on top of the webview - covering the blank white page while the remote
 * bundle loads - until this is called. Fades out for a smooth hand-off to the
 * in-app (React) splash/loading screen.
 */
export const hideNativeSplash = async () => {
  if (!isNativePlatform()) return;

  try {
    await SplashScreen.hide({ fadeOutDuration: 300 });
  } catch (error) {
    console.warn('Failed to hide native splash screen:', error);
  }
};

/**
 * Initialize mobile-specific features
 * Call this in your root layout or app entry point
 */
export const initializeMobileApp = async () => {
  if (!isNativePlatform()) return;

  try {
    // Hide splash screen after app is loaded
    await SplashScreen.hide();

    // Configure status bar
    await StatusBar.setStyle({ style: Style.Light });
    if (isAndroid()) {
      await StatusBar.setBackgroundColor({ color: '#ffffff' });
    }

    // Configure keyboard
    if (Keyboard) {
      Keyboard.setAccessoryBarVisible({ isVisible: true });
    }

    // Listen for app state changes
    App.addListener('appStateChange', ({ isActive }) => {
      console.log('App state changed. Is active:', isActive);
      // The proactive token-refresh timer (setAuthToken) is throttled/paused by
      // Android/iOS while the app is backgrounded, so after a long lock it never
      // fires and the Firebase ID token silently expires. Force a refresh on
      // resume so the first request after the user returns carries a live token
      // instead of producing a confusing "missing authorization token" 401.
      if (isActive && AUTH.currentUser) {
        refreshAuthToken(AUTH.currentUser).catch((err) =>
          console.warn('Token refresh on app resume failed:', err)
        );
      }
    });

    // Listen for back button (Android)
    if (isAndroid()) {
      App.addListener('backButton', ({ canGoBack }) => {
        if (!canGoBack) {
          App.exitApp();
        } else {
          window.history.back();
        }
      });
    }

    // Monitor network status
    Network.addListener('networkStatusChange', (status) => {
      console.log('Network status changed:', status);
    });

    console.log('Mobile app initialized successfully');
  } catch (error) {
    console.error('Error initializing mobile app:', error);
  }
};

/**
 * Get network status
 */
export const getNetworkStatus = async () => {
  if (!isNativePlatform()) {
    return { connected: navigator.onLine, connectionType: 'unknown' as const };
  }

  try {
    return await Network.getStatus();
  } catch (error) {
    console.error('Error getting network status:', error);
    return { connected: true, connectionType: 'unknown' as const };
  }
};

/**
 * Get app info
 */
export const getAppInfo = async () => {
  if (!isNativePlatform()) {
    return null;
  }

  try {
    const info = await App.getInfo();
    return info;
  } catch (error) {
    console.error('Error getting app info:', error);
    return null;
  }
};

/**
 * Open app settings (native only)
 */
export const openAppSettings = async () => {
  if (!isNativePlatform()) {
    console.warn('openAppSettings is only available on native platforms');
    return;
  }

  try {
    const { NativeSettings, AndroidSettings, IOSSettings } =
      await import('capacitor-native-settings');
    await NativeSettings.open({
      optionAndroid: AndroidSettings.ApplicationDetails,
      optionIOS: IOSSettings.App,
    });
  } catch (error) {
    console.error('Error opening app settings:', error);
  }
};

/**
 * Request specific permissions
 */
export const requestPermissions = async (permission: 'camera' | 'photos' | 'location') => {
  if (!isNativePlatform()) {
    console.warn('Permission requests are only available on native platforms');
    return { granted: false };
  }

  try {
    switch (permission) {
      case 'camera':
        // Camera permission is handled by @capacitor/camera plugin
        return { granted: true };
      case 'photos':
        // Photos permission is handled by @capacitor/camera plugin
        return { granted: true };
      case 'location': {
        const { Geolocation } = await import('@capacitor/geolocation');
        const result = await Geolocation.requestPermissions();
        return { granted: result.location === 'granted' };
      }
      default:
        return { granted: false };
    }
  } catch (error) {
    console.error('Error requesting permission:', error);
    return { granted: false };
  }
};
